
/**
 * Utility for detecting and handling social media crawlers
 */

// List of known social crawler user agents
const SOCIAL_CRAWLER_AGENTS = [
  'facebookexternalhit',
  'linkedinbot',
  'twitterbot',
  'whatsapp',
  'slackbot',
  'discordbot',
  'flickbot',
  'flicksocial',
  'flicksocialbot',
  'pinterest',
  'markybot',
  'snapchat',
  'petalbot',
  'prerender',
  'socialimagesbot'
];

/**
 * Check if the current request is from a social media crawler
 * Falls back to checking for _escaped_fragment_ query param for older crawlers
 */
export const isSocialCrawler = (): boolean => {
  if (typeof navigator === 'undefined') return false; // SSR check
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Direct user agent detection
  const isCrawlerByAgent = SOCIAL_CRAWLER_AGENTS.some(agent => 
    userAgent.includes(agent.toLowerCase())
  );
  
  if (isCrawlerByAgent) return true;
  
  // Fallback to _escaped_fragment_ check (older technique)
  if (typeof window !== 'undefined') {
    const hasEscapedFragment = window.location.search.includes('_escaped_fragment_');
    if (hasEscapedFragment) return true;
  }
  
  return false;
};

/**
 * Enhance page markup for social media crawlers
 * This adds additional metadata and context for social crawlers
 */
export const enhanceForSocialCrawlers = (): void => {
  if (typeof document === 'undefined') return; // SSR check
  
  // Check if we should enhance
  if (!isSocialCrawler()) return;
  
  console.log('Social crawler detected, enhancing page markup');
  
  // Helper to ensure a meta tag exists
  const ensureMetaTag = (name: string, content: string, isProperty = false) => {
    const selector = isProperty ? 
      `meta[property="${name}"]` : 
      `meta[name="${name}"]`;
    
    let tag = document.querySelector(selector);
    
    if (!tag) {
      tag = document.createElement('meta');
      if (isProperty) {
        tag.setAttribute('property', name);
      } else {
        tag.setAttribute('name', name);
      }
      tag.setAttribute('content', content);
      document.head.appendChild(tag);
    } else {
      tag.setAttribute('content', content);
    }
  };
  
  // Add generic crawler-friendly enhancements
  const title = document.title || 'Astrem - Website Design & Marketing';
  const description = document.querySelector('meta[name="description"]')?.getAttribute('content') ||
    'Professional website design, content creation, and marketing solutions from Astrem in Baton Rouge, LA.';
  
  ensureMetaTag('flick:card', 'summary_large_image');
  ensureMetaTag('flick:title', title);
  ensureMetaTag('flick:description', description);
  
  // Ensure OG tags for older crawlers
  ensureMetaTag('og:type', 'website', true);
  ensureMetaTag('og:title', title, true);
  ensureMetaTag('og:description', description, true);
  ensureMetaTag('og:url', window.location.href, true);
  
  console.log('Enhanced page for social crawler');
};

/**
 * Component to use in React apps to detect and handle social crawlers
 */
export const SocialCrawlerHandler = (): null => {
  if (typeof window !== 'undefined') {
    // Run once on mount
    enhanceForSocialCrawlers();
  }
  return null;
};
